import { mkdir, readFile, writeFile } from "node:fs/promises";
import { dirname, join } from "node:path";

import { resolveExecutionContext } from "../runtime/context.js";

import { resolveHomeDirectory } from "./home.js";
import type { SetupScope, SetupState } from "./types.js";

const STATE_VERSION = 1;

export async function readSetupState(
  scope: SetupScope,
  cwd: string,
  env: NodeJS.ProcessEnv,
) {
  const statePath = resolveSetupStatePath(scope, cwd, env);

  try {
    const content = await readFile(statePath, "utf8");
    const parsed = JSON.parse(content) as Partial<SetupState>;
    if (parsed.version !== STATE_VERSION) {
      return undefined;
    }
    if (!Array.isArray(parsed.agents) || !Array.isArray(parsed.commandAgents)) {
      return undefined;
    }
    if (!Array.isArray(parsed.promptAgents) || typeof parsed.packageManager !== "string") {
      return undefined;
    }
    if (typeof parsed.scope !== "string" || typeof parsed.skill !== "string") {
      return undefined;
    }
    if (typeof parsed.source !== "string" || typeof parsed.updatedAt !== "string") {
      return undefined;
    }
    return parsed as SetupState;
  } catch {
    return undefined;
  }
}

export async function writeSetupState(
  scope: SetupScope,
  cwd: string,
  env: NodeJS.ProcessEnv,
  state: Omit<SetupState, "version">,
) {
  const statePath = resolveSetupStatePath(scope, cwd, env);
  await mkdir(dirname(statePath), { recursive: true });
  await writeFile(
    statePath,
    JSON.stringify({ ...state, version: STATE_VERSION }, null, 2),
    "utf8",
  );
}

function resolveSetupStatePath(scope: SetupScope, cwd: string, env: NodeJS.ProcessEnv) {
  if (scope === "global") {
    const stateHome = env.XDG_STATE_HOME ?? join(resolveHomeDirectory(env), ".local", "state");
    return join(stateHome, "emergent-thinking", "install-state.json");
  }

  const context = resolveExecutionContext(cwd);
  return join(context.storageRoot, "install-state.json");
}
